import React, { useState, useEffect } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from 'recharts'
import Grid from '@material-ui/core/Grid'
import { Typography, Box } from '@material-ui/core'

import { Moment } from 'config/moment'
import { useApiCall } from 'hooks'
import { ERRORS } from 'consts'
import { snackbar } from 'components'

import {
  GetDataUserStatusDashboadResponse,
  DataUserStatusDashboad,
  ParametersDownload,
} from '../types'
import { getUserStatus } from '../api'

const USER_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  ABANDONER: 'ABANDONER',
  LOST: 'LOST',
  NOT_YET: 'NOT_YET',
}

const STATUS_ORDER = [
  USER_STATUS.ACTIVE,
  USER_STATUS.INACTIVE,
  USER_STATUS.ABANDONER,
  USER_STATUS.LOST,
  USER_STATUS.NOT_YET,
]

interface propsUsersStatus {
  startDate: Moment
  endDate: Moment
}

const UserStatus: React.FC<propsUsersStatus> = ({ startDate, endDate }) => {
  const [data, setData] = useState<DataUserStatusDashboad[] | []>([])
  const [requestUsersTags] = useApiCall<ParametersDownload, GetDataUserStatusDashboadResponse>(() =>
    getUserStatus({ startDate, endDate }),
  )

  useEffect(() => {
    requestUsersTags({ startDate, endDate })
      .then(resp => {
        const sortData = STATUS_ORDER.map(code =>
          resp.data.find(obj => obj.code === code),
        ).filter((obj): obj is DataUserStatusDashboad => Boolean(obj))
        const sumTags = sortData.reduce(
          (accumulator, currentValue) => accumulator + currentValue.value,
          0,
        )
        const dataWithPercent = sortData.map(e => {
          return { ...e, percent: sumTags ? e.value / sumTags : 0 }
        })
        setData(dataWithPercent)
      })
      .catch(() => {
        setData([])
        snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, height, percent } = props
    return (
      <text
        x={Number(x) + Number(width) / 2}
        y={Number(y) + Number(height) / 2}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {`${(Number(percent) * 100).toFixed(0)}%`}
      </text>
    )
  }

  return (
    <Grid
      container
      spacing={2}
      style={{ background: '#fff', boxShadow: '0 2px 7px 0 rgba(0,0,0,.06)' }}
    >
      <Grid item xs={12} sm={12} md={8}>
        <ResponsiveContainer width="100%">
          <BarChart
            data={data}
            margin={{
              top: 5,
              right: 50,
              left: 20,
              bottom: 5,
            }}
            barSize={60}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Bar dataKey="value" fill="#e78923" label={{ fill: 'black' }}>
              <LabelList dataKey="percent" content={renderCustomizedLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <Typography component="div" color="textSecondary">
          <Box fontWeight="fontWeightMedium" display="inline">
            Activo:
          </Box>{' '}
          escaneos en los últimos 90 días.
        </Typography>
        <Typography component="div" color="textSecondary" style={{ marginTop: 20 }}>
          <Box fontWeight="fontWeightMedium" display="inline">
            Inactivo:
          </Box>{' '}
          sin escaneos entre 91 y 179 días (solo si ya visitó alguna vez).
        </Typography>
        <Typography component="div" color="textSecondary" style={{ marginTop: 20 }}>
          <Box fontWeight="fontWeightMedium" display="inline">
            Abandonador:
          </Box>{' '}
          sin escaneos entre 180 y 364 días (solo si ya visitó alguna vez).
        </Typography>
        <Typography component="div" color="textSecondary" style={{ marginTop: 20 }}>
          <Box fontWeight="fontWeightMedium" display="inline">
            Perdido:
          </Box>{' '}
          sin escaneos durante 365 días o más, o 365+ días desde el alta sin nunca haber visitado.
        </Typography>
        <Typography component="div" color="textSecondary" style={{ marginTop: 20 }}>
          <Box fontWeight="fontWeightMedium" display="inline">
            Sin visitas:
          </Box>{' '}
          se registró y nunca escaneó (hasta 364 días desde el alta; luego pasa a Perdido).
        </Typography>
      </Grid>
    </Grid>
  )
}
export { UserStatus }
